/**
 * RampartSkillEffect — Earth tree wall path evolution (Fork 1) active skill.
 *
 * Evolution of Wall. Instant activation. Raises a 5-tile-wide stone barrier
 * perpendicular to the player's facing direction, 1 tile in front. 150 HP,
 * expires after 12 seconds. Two active at once (LRU eviction when a third
 * would be placed). Enemies striking the Rampart receive 1-tile knockback.
 *
 * Warded carry-through: when resolver.hasEffect('wall', 'proximityDamageReduction')
 * returns true (Warded was unlocked before this evolution), the TerrainConfig is
 * populated with proximityEffect: { radiusTiles: 1.5, damageReduction: 0.1 }.
 *
 * onContactEffect: { knockbackTiles: 1 } is always included in TerrainConfig.
 * TerrainConstructionSystem handles the physics knockback at the integration layer.
 *
 * LRU management: _activeWallIds is a queue (index 0 = oldest). When placing
 * would exceed maxActive (2), the oldest ID is shifted and removed before the
 * new wall is placed. This matches the WallSkillEffect single-slot pattern,
 * extended to a two-slot queue.
 *
 * Upgrade keys on scope 'wall' (inherited from Warded only — Rampart bakes its
 * own stats and does not inherit Reinforced's additive hp/duration deltas):
 * - proximityDamageReduction: flag/value (Warded carry-through)
 *
 * Extends InstantSkillEffect; overrides cooldown and update for timer tracking.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Wall Path — Rampart
 */

import { InstantSkillEffect } from '../../InstantSkillEffect.js';

// ---------------------------------------------------------------------------
// Terrain config interface (extends WallTerrainConfig with onContactEffect)
// ---------------------------------------------------------------------------

export interface RampartTerrainConfig {
  readonly type: 'wall';
  readonly position: { x: number; y: number };
  readonly widthTiles: number;
  readonly heightTiles: number;
  readonly hp: number;
  readonly durationMs: number;
  readonly sourceSkillId: string;
  readonly blocksMovement: boolean;
  readonly blocksProjectiles: boolean;
  /** Contact knockback applied to any enemy that melee-hits this terrain. */
  readonly onContactEffect?: {
    readonly knockbackTiles: number;
  };
  readonly proximityEffect?: {
    readonly radiusTiles: number;
    readonly damageReduction: number;
  };
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface RampartDeps {
  readonly resolver: {
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly placement: {
    /**
     * Returns the wall's center position (1 tile in front of the player,
     * perpendicular to facing direction) and the 5-tile footprint positions
     * that the pathfinding grid must mark impassable.
     */
    getWallPlacement(): {
      position: { x: number; y: number };
      footprintTiles: Array<{ x: number; y: number }>;
    };
  };
  readonly terrain: {
    place(config: RampartTerrainConfig): string;
    remove(terrainId: string): void;
  };
  readonly pathfinding: {
    markImpassable(positions: Array<{ x: number; y: number }>): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface RampartConfig {
  /** HP of the placed Rampart (baked-in, not affected by Reinforced). */
  readonly hp: number;
  /** Duration in ms before expiry. */
  readonly durationMs: number;
  /** Cooldown in ms after each activation (medium tier). */
  readonly cooldownMs: number;
  /** Width of the Rampart in tiles. */
  readonly widthTiles: number;
  /** Height of the Rampart in tiles. */
  readonly heightTiles: number;
  /** Maximum simultaneously active Ramparts before LRU eviction. */
  readonly maxActive: number;
  /** Tiles of knockback applied to enemies on contact. */
  readonly knockbackTiles: number;
  /** Proximity DR radius in tiles (Warded carry-through). */
  readonly proximityRadiusTiles: number;
  /** Proximity DR value when Warded is active. */
  readonly proximityDamageReduction: number;
}

const DEFAULT_CONFIG: RampartConfig = {
  hp: 150,
  durationMs: 12000,
  cooldownMs: 800,    // medium cooldown tier
  widthTiles: 5,
  heightTiles: 1,
  maxActive: 2,
  knockbackTiles: 1,
  proximityRadiusTiles: 1.5,
  proximityDamageReduction: 0.1,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class RampartSkillEffect extends InstantSkillEffect {
  readonly skillId = 'wall_evolved';

  private readonly deps: RampartDeps;
  private readonly config: RampartConfig;

  // Cooldown timer (ms remaining until ready)
  private _cooldownTimer = 0;

  // LRU queue: index 0 is the oldest active wall.
  private _activeWallIds: string[] = [];

  // Telemetry
  private _wallsPlacedCount = 0;
  private _lastTerrainConfig: RampartTerrainConfig | null = null;

  constructor(deps: RampartDeps, config: Partial<RampartConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  override get cooldown(): number { return this._cooldownTimer; }

  /** Ordered queue of active Rampart terrain IDs (oldest first). */
  get activeWallIds(): readonly string[] { return this._activeWallIds; }

  get wallsPlacedCount(): number { return this._wallsPlacedCount; }

  /** The TerrainConfig used for the most recently placed Rampart. */
  get lastTerrainConfig(): RampartTerrainConfig | null { return this._lastTerrainConfig; }

  // --- InstantSkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownTimer <= 0;
  }

  activate(): boolean {
    if (this._cooldownTimer > 0) return false;

    // LRU eviction: if at capacity, remove the oldest wall before placing a new one
    if (this._activeWallIds.length >= this.config.maxActive) {
      const oldest = this._activeWallIds.shift()!;
      this.deps.terrain.remove(oldest);
    }

    // Warded carry-through: Rampart inherits the proximity DR if Warded was unlocked
    const hasWarded = this.deps.resolver.hasEffect('wall', 'proximityDamageReduction');
    const proximityEffect = hasWarded
      ? {
          radiusTiles: this.config.proximityRadiusTiles,
          damageReduction: this.config.proximityDamageReduction,
        }
      : undefined;

    // Compute placement position and footprint via game layer
    const { position, footprintTiles } = this.deps.placement.getWallPlacement();

    const terrainConfig: RampartTerrainConfig = {
      type: 'wall',
      position,
      widthTiles: this.config.widthTiles,
      heightTiles: this.config.heightTiles,
      hp: this.config.hp,
      durationMs: this.config.durationMs,
      blocksMovement: true,
      blocksProjectiles: true,
      sourceSkillId: 'rampart',
      onContactEffect: { knockbackTiles: this.config.knockbackTiles },
      ...(proximityEffect !== undefined ? { proximityEffect } : {}),
    };

    const terrainId = this.deps.terrain.place(terrainConfig);
    this._activeWallIds.push(terrainId);
    this._lastTerrainConfig = terrainConfig;
    this._wallsPlacedCount++;

    // Synchronous pathfinding update — footprint tiles are now impassable
    this.deps.pathfinding.markImpassable(footprintTiles);

    // Start cooldown
    this._cooldownTimer = this.config.cooldownMs;

    return true;
  }

  override update(delta: number): void {
    if (this._cooldownTimer > 0) {
      this._cooldownTimer -= delta;
      if (this._cooldownTimer < 0) this._cooldownTimer = 0;
    }
  }
}
